import { useEffect, useState } from "react";
import { collection, doc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useCompany } from "../contexts/CompanyContext";
import "./SmartAssistancePage.css";
import { ACCESS_NO_END, employeeAccessUpdate, isEmployeeAccessActive } from "../utils/assistanceAccess";
import AssistanceClients from "../components/AssistanceClients";
import AssistanceSchedule from "../components/AssistanceSchedule";
import AssistanceOverview from "../components/AssistanceOverview";
import AssistanceDailyNotes from "../components/AssistanceDailyNotes";
import {
  readSchedulingEmployees,
} from "../utils/schedulingPopupData";

const assistanceSections = [
  { id: "schedule", label: "Schema" },
  { id: "overview", label: "Översikt" },
  { id: "clients", label: "Mina klienter" },
  { id: "rounds", label: "Ronder" },
  { id: "plans", label: "Genomförandeplaner" },
  { id: "notes", label: "Daganteckningar" },
  { id: "medications", label: "Mediciner" },
  { id: "reports", label: "Rapporter" },
];

export default function SmartAssistancePage() {
  const { activeCompany } = useCompany();
  const companyUid = activeCompany?.companyAccountUid;
  const [employeeData, setEmployeeData] = useState(null);
  const [personnelError, setPersonnelError] = useState("");
  const [activeSection, setActiveSection] = useState("schedule");
  const [roundSearch, setRoundSearch] = useState("");
  const [savingRoundIds, setSavingRoundIds] = useState([]);
  const [roundError, setRoundError] = useState("");
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setRoundSearch("");
    setRoundError("");
    setEmployeeData(null);
    setPersonnelError("");
    if (!companyUid) return;

    const unsubscribeEmployees = onSnapshot(
      collection(db, "users", companyUid, "employed"),
      (snapshot) => {
        setEmployeeData({ companyUid, employees: readSchedulingEmployees(snapshot) });
        setPersonnelError("");
      },
      (error) => {
        console.error("Kunde inte hämta användare:", error);
        setEmployeeData(null);
        setPersonnelError("Kunde inte hämta personalen. Ladda om sidan för att försöka igen.");
      }
    );
    return () => unsubscribeEmployees();
  }, [companyUid]);

  // Use employed documents so the personnel app can read its own access status.
  const personnel = employeeData?.companyUid === companyUid ? employeeData.employees : [];
  const roundPersonnel = personnel.filter((employee) => employee.smartAssistanceRounds?.selected === true);
  const availablePersonnel = personnel.filter((employee) =>
    employee.smartAssistanceRounds?.selected !== true &&
    `${employee.name} ${employee.email}`.toLocaleLowerCase("sv").includes(roundSearch.trim().toLocaleLowerCase("sv"))
  );

  const saveRoundAccess = async (employee, enabled, selected = true) => {
    const savingCompanyUid = companyUid;
    setRoundError("");
    setSavingRoundIds((ids) => [...ids, employee.id]);
    try {
      await updateDoc(doc(db, "users", savingCompanyUid, "employed", employee.id), employeeAccessUpdate(employee, {
        enabled,
        opensAt: enabled ? new Date().toISOString() : null,
        closesAt: enabled ? ACCESS_NO_END : null,
      }, selected));
    } catch (error) {
      console.error("Kunde inte spara åtkomst till Ronder:", error);
      setRoundError("Kunde inte spara ändringen. Försök igen.");
    } finally {
      setSavingRoundIds((ids) => ids.filter((id) => id !== employee.id));
    }
  };

  return (
    <section className="smart-assistance">
      <div className="smart-assistance__header">
      {activeCompany?.companyName && <p className="smart-assistance__company">
        Hantera smart assistans för <strong>{activeCompany.companyName}</strong>
      </p>}
      <nav className="smart-assistance__menu" aria-label="Smart assistans">
        {assistanceSections.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={activeSection === id ? "is-active" : ""}
            aria-pressed={activeSection === id}
            aria-controls="smart-assistance-content"
            onClick={() => setActiveSection(id)}
          >
            {label}
          </button>
        ))}
      </nav>
      </div>
      <div id="smart-assistance-content" className="smart-assistance__content">
      {activeSection !== "schedule" && <h2>{assistanceSections.find(({ id }) => id === activeSection).label}</h2>}
      {activeSection === "schedule" ? <AssistanceSchedule key={companyUid || "no-company"} companyUid={companyUid} personnel={personnel} personnelError={personnelError} personnelLoading={Boolean(companyUid) && !personnelError && employeeData?.companyUid !== companyUid} /> : activeSection === "clients" ? <AssistanceClients key={companyUid || "no-company"} companyUid={companyUid} /> : activeSection === "rounds" ? <>
        <p>Välj personal och aktivera deras åtkomst till Ronder. Tillagd personal är avaktiverad från början.</p>
        <label className="smart-assistance__search">
          Lägg till personal
          <input type="search" value={roundSearch} onChange={(event) => setRoundSearch(event.target.value)} placeholder="Sök namn eller e-post" />
        </label>
        {employeeData?.companyUid !== companyUid ? <p>Hämtar personal…</p> : <div className="smart-assistance__personnel-picker">
          {availablePersonnel.map((employee) => <button type="button" key={employee.id} disabled={savingRoundIds.includes(employee.id)} onClick={() => saveRoundAccess(employee, false)}>
            {employee.name}{employee.email && <small>{employee.email}</small>}
          </button>)}
          {availablePersonnel.length === 0 && <p>Ingen personal att lägga till.</p>}
        </div>}
        {roundError && <p role="alert">{roundError}</p>}
        <div className="smart-assistance__round-list">
          {roundPersonnel.map((employee) => {
            const enabled = isEmployeeAccessActive(employee, now);
            const saving = savingRoundIds.includes(employee.id);
            return <div className="smart-assistance__round-row" key={employee.id}>
              <div><strong>{employee.name}</strong><small>{employee.email}</small></div>
              <span className={enabled ? "smart-assistance__status is-enabled" : "smart-assistance__status"}>{enabled ? "Aktiverad" : "🔒 Avaktiverad"}</span>
              <button type="button" disabled={saving} onClick={() => saveRoundAccess(employee, !enabled)}>{saving ? "Sparar…" : enabled ? "Avaktivera" : "Aktivera"}</button>
            </div>;
          })}
          {roundPersonnel.length === 0 && <p>Ingen personal har lagts till i Ronder ännu.</p>}
        </div>
        <p>Avaktiverad åtkomst innebär att personalens flik ska visas med lås och inte gå att öppna.</p>
      </> : activeSection === "notes" ? <AssistanceDailyNotes key={companyUid || "no-company"} companyUid={companyUid} /> : activeSection === "overview" ? <AssistanceOverview key={companyUid || "no-company"} companyUid={companyUid} /> : <p>Här kommer {assistanceSections.find(({ id }) => id === activeSection).label.toLowerCase()} att finnas.</p>}
      </div>
    </section>
  );
}
